import { onlineManager } from '@tanstack/react-query';
import { del, get, keys, set, update } from 'idb-keyval';

/**
 * The browser storage the identity code reaches for, in one place (MAP 225a).
 *
 * Every accessor here can be missing or can throw: a private window, blocked site data, a
 * test process with no DOM. Callers treat "unavailable" as an ordinary answer.
 */

/** A key-value store: the default idb-keyval store in the app, a `Map` in tests. */
export interface Kv {
  keys(): Promise<IDBValidKey[]>;
  get(key: IDBValidKey): Promise<unknown>;
  set(key: IDBValidKey, value: unknown): Promise<void>;
  /** Read and write one key in a single readwrite transaction. */
  update(key: IDBValidKey, updater: (current: unknown) => unknown): Promise<void>;
  del(key: IDBValidKey): Promise<void>;
}

/** IndexedDB's `keyval-store`/`keyval`, where the query cache and the drafts live. */
export const idbKv: Kv = {
  keys: () => keys(),
  get: (key) => get(key),
  set: (key, value) => set(key, value),
  update: (key, updater) => update(key, updater),
  del: (key) => del(key),
};

/** `localStorage`, or undefined where reading it throws or there is none. */
export function safeLocalStorage(): Storage | undefined {
  try {
    return globalThis.localStorage ?? undefined;
  } catch {
    return undefined;
  }
}

/** The slice of `navigator.locks` this app uses: an exclusive, queued lock per name. */
export interface ExclusiveLocks {
  request<T>(
    name: string,
    options: { signal?: AbortSignal },
    callback: () => Promise<T>,
  ): Promise<T>;
}

/** This browser's Web Locks, or undefined where there are none (older browsers, tests). */
export function browserLocks(): ExclusiveLocks | undefined {
  return (globalThis.navigator as { locks?: ExclusiveLocks } | undefined)?.locks;
}

/**
 * Run `attempt` whenever the signal returns or the tab is shown: the two moments a store
 * that could not be read is likely to be readable again, and a queue that stopped is worth
 * trying again. Usable with no DOM at all (a test process). Returns the unsubscribe.
 */
export function onSignalOrVisible(attempt: () => void): () => void {
  const offline = onlineManager.subscribe((online) => {
    if (online) attempt();
  });
  const doc = globalThis.document as Document | undefined;
  const onVisible = () => {
    if (doc?.visibilityState === 'visible') attempt();
  };
  doc?.addEventListener('visibilitychange', onVisible);
  return () => {
    offline();
    doc?.removeEventListener('visibilitychange', onVisible);
  };
}
